import React, {useEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import {Provider, useStore} from "react-redux";
import {Router, useHistory} from 'react-router-dom';

// Configuration (Keep these outside the component)
const MAPBOX_VERSION = 'v3.4.0';
const MAPBOX_CSS_URL = `https://api.mapbox.com/mapbox-gl-js/${MAPBOX_VERSION}/mapbox-gl.css`;
const MAPBOX_JS_URL = `https://api.mapbox.com/mapbox-gl-js/${MAPBOX_VERSION}/mapbox-gl.js`;
const MAPBOX_ACCESS_TOKEN = 'pk.eyJ1IjoiY2FwaXRvbGRldmVsb3BtZW50IiwiYSI6ImNtZ2F2aHY4aDB1dmMyanM4Mmg2d2Z3NTkifQ.2Fk3QSgkLrTvYbclwzKsbQ';

declare global {
    interface Window {
        mapboxgl: any;
    }
}

export interface GeoPoint {
    lat: number;
    long: number;
    color?: string,
    popupRender?: (point: GeoPoint) => JSX.Element
    popupTrigger?: 'click' | 'hover'
}


interface BoundingBox {
    latMin: number;
    latMax: number;
    longMin: number;
    longMax: number;
    /** Bounding box in Mapbox GL JS format: [longMin, latMin, longMax, latMax] */
    mapboxBounds: [number, number, number, number];
}

interface IProps {
    initialCenter?: GeoPoint
    points?: GeoPoint[]
    initialZoom?: number;
    onClick?: (coords: GeoPoint) => void
    width?: number | string
    height?: number | string
}

const MIN_SPAN_DEGREES = 0.01;

interface MarkerRefs {
    [key: string]: any | null;
}

const Map: React.FC<IProps> = ({
                                   initialCenter = {lat: 48, long: 17},
                                   initialZoom = 3,
                                   onClick,
                                   points,
                                   width = '100%',
                                   height = 500
                               }) => {
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<any>(null);
    const markersRef = useRef<MarkerRefs>({});
    const [isLoaded, setIsLoaded] = useState(false);
    const [isReady, setIsReady] = useState(false);


    const store = useStore();
    const history = useHistory();
    const timeoutRef = useRef<number | null>(null);


    // --- Utility Function to Inject Scripts and Styles (omitted for brevity) ---
    // ... loadMapbox function remains the same ...
    const loadMapbox = () => {
        if (!document.querySelector(`link[href="${MAPBOX_CSS_URL}"]`)) {
            const link = document.createElement('link');
            link.href = MAPBOX_CSS_URL;
            link.rel = 'stylesheet';
            document.head.appendChild(link);
        }
        if (!document.querySelector(`script[src="${MAPBOX_JS_URL}"]`)) {
            const script = document.createElement('script');
            script.src = MAPBOX_JS_URL;
            script.onload = () => setIsLoaded(true);
            document.head.appendChild(script);
        } else {
            setIsLoaded(true);
        }
    };

    // --- Effect 1: Load Scripts on Mount (omitted for brevity) ---
    useEffect(() => {
        loadMapbox();
    }, []);

    // --- Effect 2: Initialize Map when Script is Loaded (omitted for brevity) ---
    // ... (Map initialization logic remains the same) ...
    useEffect(() => {
        const {lat, long} = initialCenter;

        if (isLoaded && window.mapboxgl && mapContainerRef.current && !mapRef.current) {

            window.mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

            mapRef.current = new window.mapboxgl.Map({
                container: mapContainerRef.current,
                style: 'mapbox://styles/mapbox/streets-v12',
                center: [long, lat],
                zoom: initialZoom
            });

            mapRef.current?.on('load', () => {
                setIsReady(true);
            });
        }

        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, [isLoaded]);

    // --- NEW Effect 3: Add Map Click Listener ---
    useEffect(() => {
        const map = mapRef.current;

        if (map) {
            // Define the click handler function
            const clickHandler = (e: any) => {
                const {lng, lat} = e.lngLat;

                // Call the prop function to return coordinates to the parent component
                onClick?.({lat: lat, long: lng});
            };

            // Attach the listener
            map.on('click', clickHandler);

            // Cleanup: Remove the listener when the component unmounts or dependencies change
            return () => {
                map.off('click', clickHandler);
            };
        }
    }, [mapRef.current, onClick]); // Re-run if map instance or onClick handler changes

    const useDeepCompareMemoize = (value: any) => {
        const ref = useRef(value);

        // Only update the ref if the contents of 'value' have deeply changed
        if (JSON.stringify(value) !== JSON.stringify(ref.current)) {
            ref.current = value;
        }

        return ref.current;
    }

    const stablePoints = useDeepCompareMemoize(points)
    // --- Effect 4: Handle Marker and Map View Updates (omitted for brevity) ---
    // ... (Marker logic remains the same, you can update initialCenter based on a click in the parent component) ...
    useEffect(() => {

        const map = mapRef.current;

        markersRef.current = {}; // Reset the ref object

        // Clear any pending timeout when the effect starts or cleans up
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        Object.values(markersRef.current).forEach(marker => {
            if (marker) {
                marker.remove();
            }
        });

        if (points) {
            points?.forEach(point => {
                const {lat, long} = point;

                if (map && lat && long) {
                    const coordinates: [number, number] = [long, lat];

                    const newMarker = new window.mapboxgl.Marker({
                        color: point.color || '#3FB1CE', // Use any hex code or CSS color name
                    })
                        .setLngLat(coordinates)
                        .addTo(map);

                    // Store the new marker reference using its unique ID as the key
                    markersRef.current[point.lat + '_' + point.long] = newMarker;

                    // 2. Handle Popup
                    if (point.popupRender) {
                        const trigger = point.popupTrigger || 'click'; // Default to click

                        const container = document.createElement('div');
                        const WrappedComponent = (
                            <Provider store={store}>
                                <Router history={history}>
                                    {point.popupRender(point)}
                                </Router>
                            </Provider>
                        );
                        ReactDOM.render(WrappedComponent, container);

                        const popup = new window.mapboxgl.Popup({
                            closeButton: trigger === 'click', // Show close button only on click
                            closeOnClick: trigger === 'click', // Clicking map closes popup only on click mode
                            offset: 25,
                            maxWidth: 'none'
                        }).setDOMContent(container);

                        const markerElement = newMarker.getElement();

// Change cursor to pointer when hovering over the marker
                        markerElement.style.cursor = 'pointer';

// If you want it to change only when the popup is active:
                        markerElement.addEventListener('mouseenter', () => {
                            map.getCanvas().style.cursor = 'pointer';
                        });
                        markerElement.addEventListener('mouseleave', () => {
                            map.getCanvas().style.cursor = '';
                        });

                        if (trigger === 'hover') {
                            // --- HOVER LOGIC ---
                            const handleMouseEnter = () => {
                                if (timeoutRef.current !== null) {
                                    clearTimeout(timeoutRef.current);
                                    timeoutRef.current = null;
                                }
                                popup.setLngLat(coordinates).addTo(map);
                            };

                            const handleMouseLeave = () => {
                                timeoutRef.current = setTimeout(() => {
                                    popup.remove();
                                    timeoutRef.current = null;
                                }, 150) as unknown as number;
                            };

                            const handlePopupMouseEnter = () => {
                                if (timeoutRef.current !== null) {
                                    clearTimeout(timeoutRef.current);
                                    timeoutRef.current = null;
                                }
                            };

                            markerElement.addEventListener('mouseenter', handleMouseEnter);
                            markerElement.addEventListener('mouseleave', handleMouseLeave);
                            container.addEventListener('mouseenter', handlePopupMouseEnter);
                            container.addEventListener('mouseleave', handleMouseLeave); // Re-use standard leave

                            newMarker.__cleanup = () => {
                                markerElement.removeEventListener('mouseenter', handleMouseEnter);
                                markerElement.removeEventListener('mouseleave', handleMouseLeave);
                                container.removeEventListener('mouseenter', handlePopupMouseEnter);
                                container.removeEventListener('mouseleave', handleMouseLeave);
                            };
                        } else {
                            // --- CLICK LOGIC (Standard Mapbox) ---
                            newMarker.setPopup(popup);
                            // In click mode, Mapbox handles the toggle automatically
                        }
                    }
                }
            })

            const boundingBox = calculateBoundingBox(points);

            if (boundingBox && map) {
                map.fitBounds(boundingBox.mapboxBounds, {
                    padding: 20,
                    duration: 1500,
                });
            }
        }

        return () => {
            Object.values(markersRef.current).forEach(marker => {
                if (marker) {
                    marker.remove();
                }
            });
        };
    }, [stablePoints, isReady]);


    /**
     * Calculates the smallest bounding box that contains all geocoordinates.
     *
     * @param points An array of GeoPoint objects.
     * @returns The bounding box coordinates in both standard and Mapbox formats, or null.
     */
    const calculateBoundingBox = (points: GeoPoint[]): BoundingBox | null => {
        if (points.length === 0) {
            return null;
        }

        let latMin = points[0].lat;
        let latMax = points[0].lat;
        let longMin = points[0].long;
        let longMax = points[0].long;

        for (const point of points) {
            latMin = Math.min(latMin, point.lat);
            latMax = Math.max(latMax, point.lat);
            longMin = Math.min(longMin, point.long);
            longMax = Math.max(longMax, point.long);
        }

        // 2. Enforce Minimum Span
        let latSpan = latMax - latMin;
        let longSpan = longMax - longMin;

        // Check if the current span is too small
        if (latSpan < MIN_SPAN_DEGREES || longSpan < MIN_SPAN_DEGREES) {

            // Calculate the center of the current, tiny box
            const latCenter = (latMax + latMin) / 2;
            const longCenter = (longMax + longMin) / 2;

            // Determine how much to pad on each side
            const halfPad = MIN_SPAN_DEGREES / 2;

            // Recalculate the box using the center and the minimum span
            latMin = latCenter - halfPad;
            latMax = latCenter + halfPad;
            longMin = longCenter - halfPad;
            longMax = longCenter + halfPad;
        }

        return {
            latMin,
            latMax,
            longMin,
            longMax,
            // Mapbox GL JS uses [West/longMin, South/latMin, East/longMax, North/latMax]
            mapboxBounds: [longMin, latMin, longMax, latMax],
        };
    }

    return (
        <div className={'h-100'}>
            {!isLoaded && (
                <p>Loading map library...</p>
            )}
            <div
                ref={mapContainerRef}
                style={{
                    width: typeof width === 'string' ? (width || '100%') : (width + 'px'),
                    height: typeof height === 'string' ? height : height + 'px'
                }}
                className="map-container"
            />
        </div>
    );
};

export default Map;